import React, { useState } from 'react';
import { X, Building2, Users, Mail, User, Phone } from 'lucide-react';
import api, { apiErrorText } from '../utils/api';
import {
  getCountryPhoneCodes,
  DEFAULT_PHONE_COUNTRY,
  formatPhoneWithCountry
} from '../utils/phone';
import { useTranslation } from 'react-i18next';

interface DemoRequestDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const DemoRequestDialog: React.FC<DemoRequestDialogProps> = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    contact_email: '',
    contact_phone_country: DEFAULT_PHONE_COUNTRY,
    contact_phone_number: '',
    industry: 'General',
    company_size: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const companySizes = [
    { value: '1-10', labelKey: 'demoDialog.companySizes.size1_10' },
    { value: '11-50', labelKey: 'demoDialog.companySizes.size11_50' },
    { value: '51-200', labelKey: 'demoDialog.companySizes.size51_200' },
    { value: '201-500', labelKey: 'demoDialog.companySizes.size201_500' },
    { value: '500+', labelKey: 'demoDialog.companySizes.size500_plus' }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await api.post('/api/demo-requests', {
        ...formData,
        contact_phone: formData.contact_phone_number
          ? formatPhoneWithCountry(formData.contact_phone_country, formData.contact_phone_number)
          : undefined,
      });
      setSuccess(true);
      setTimeout(() => {
        onClose();
        setSuccess(false);
        setFormData({
          company_name: '',
          contact_name: '',
          contact_email: '',
          contact_phone_country: DEFAULT_PHONE_COUNTRY,
          contact_phone_number: '',
          industry: '',
          company_size: '',
          message: ''
        });
      }, 2000);
    } catch (err: any) {
      setError(apiErrorText(err.response?.data?.detail) || t('demoDialog.errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{t('demoDialog.title')}</h2>
            <p className="text-sm text-gray-600 mt-1">
              {t('demoDialog.subtitle')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mx-6 mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-800 font-medium">
              {t('demoDialog.success')}
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mx-6 mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Company Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Building2 className="inline w-4 h-4 mr-1" />
              {t('demoDialog.fields.companyName')}
            </label>
            <input
              type="text"
              required
              value={formData.company_name}
              onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              className="input w-full"
              placeholder={t('demoDialog.placeholders.companyName') as string}
            />
          </div>

          {/* Contact Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <User className="inline w-4 h-4 mr-1" />
              {t('demoDialog.fields.contactPerson')}
            </label>
            <input
              type="text"
              required
              value={formData.contact_name}
              onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
              className="input w-full"
              placeholder={t('demoDialog.placeholders.contactName') as string}
            />
          </div>

          {/* Contact Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Mail className="inline w-4 h-4 mr-1" />
              {t('demoDialog.fields.email')}
            </label>
            <input
              type="email"
              required
              value={formData.contact_email}
              onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
              className="input w-full"
              placeholder="example@company.com"
            />
          </div>

          {/* Contact Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Phone className="inline w-4 h-4 mr-1" />
              {t('demoDialog.fields.phone')}
            </label>
            <div className="flex gap-3">
              <div className="w-32 flex-shrink-0">
                <select
                  className="input w-full"
                  value={formData.contact_phone_country}
                  onChange={(e) => setFormData({ ...formData, contact_phone_country: e.target.value })}
                >
                  {getCountryPhoneCodes(i18n.language).map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.label}
                    </option>
                  ))}
                </select>
              </div>
              <input
                type="tel"
                inputMode="tel"
                pattern="\d{5,14}"
                value={formData.contact_phone_number}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    contact_phone_number: e.target.value.replace(/[^\d]/g, ''),
                  })
                }
                className="input flex-1"
                placeholder={t('demoRequest.phonePlaceholder')}
              />
            </div>
            {formData.contact_phone_number && (
              <p className="text-xs text-gray-500 mt-1">
                {formatPhoneWithCountry(
                  formData.contact_phone_country,
                  formData.contact_phone_number
                )}
              </p>
            )}
          </div>

          {/* Company Size */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Users className="inline w-4 h-4 mr-1" />
              {t('demoDialog.fields.companySize')}
            </label>
            <select
              required
              value={formData.company_size}
              onChange={(e) => setFormData({ ...formData, company_size: e.target.value })}
              className="input w-full"
            >
              <option value="">{t('demoDialog.placeholders.companySize')}</option>
              {companySizes.map(({ value, labelKey }) => (
                <option key={value} value={value}>
                  {t(labelKey)}
                </option>
              ))}
            </select>
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('demoDialog.fields.additionalInfo')}
            </label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="input w-full"
              rows={4}
              placeholder={t('demoDialog.placeholders.message') as string}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn-outline flex-1"
              disabled={loading}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary flex-1"
              disabled={loading}
            >
              {loading ? t('demoDialog.sending') : t('demoDialog.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DemoRequestDialog;
